import { ConnectProto } from "@racoon/api";
import { Namespace, useTypedT } from "@racoon/i18n";
import { BrandIcImportant, IcGarbageTruck, IcProfile } from "@racoon/icons";
import { CountBadge, DatePicker, Input, cn } from "@racoon/ui";
import { Check, Search } from "lucide-react";
import * as React from "react";
import { shiftStatusTone } from "~/pages/dashboard/autopark/helpers";
import { ModeTabs } from "~/shared/ui/mode-tabs";
import { findPreferredShift } from "../../hooks/msw-route-list/useMswRouteListState";
import type { SidebarMode, SidebarSection } from "./msw-route-list.types";

type Props = {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  mode: SidebarMode;
  onModeChange: (mode: SidebarMode) => void;
  sections: SidebarSection[];
  selectedId: bigint | null;
  onSelect: (id: bigint, shiftId?: bigint) => void;
  routesByShiftId: Map<bigint, ConnectProto.Route[]>;
  isShiftFlagged?: (shift: ConnectProto.DriverOnShift) => boolean;
  /**
   * `routes`: a shift-status dot and the assigned-jobs counter on each row.
   * `ttn`: neither — a green check once the row's TTN is approved, the flag until then.
   */
  variant?: "routes" | "ttn";
};

export const MswRouteListSidebar: React.FC<Props> = ({
  selectedDate,
  onDateChange,
  searchValue,
  onSearchChange,
  mode,
  onModeChange,
  sections,
  selectedId,
  onSelect,
  routesByShiftId,
  isShiftFlagged,
  variant = "routes",
}) => {
  const t = useTypedT(Namespace.Routes);

  return (
    <aside className="w-full h-full min-w-0 min-h-0 flex flex-col border-r border-ds-border-primary bg-white">
      <div className="h-16 shrink-0 px-4 w-full flex items-center justify-center border-b border-ds-border-primary">
        <DatePicker
          showDayNav
          hideIcon
          value={selectedDate}
          onChange={onDateChange}
          navWrapperClassName="flex w-full items-center gap-1"
          className="flex h-8 w-full min-w-0 gap-1 bg-white !px-1 !text-xs leading-none"
        />
      </div>

      <div className="shrink-0 flex px-2 py-4 border-b border-ds-border-primary">
        <ModeTabs
          variant="outline"
          testId="sidebar-mode"
          value={mode}
          onChange={onModeChange}
          options={[
            {
              value: "trucks",
              label: t("sidebarTrucks"),
              icon: <IcGarbageTruck className="h-5 w-5" />,
            },
            {
              value: "drivers",
              label: t("sidebarDrivers"),
              icon: <IcProfile className="h-5 w-5" />,
            },
          ]}
        />
      </div>

      <div className="shrink-0 px-2 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-icon-default" />
          <Input
            size="sm"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t("search")}
            className="h-8 pl-8 placeholder:text-ds-text-secondary"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-2 pb-2">
        {sections.map((section) => (
          <div key={section.key} data-testid="sidebar-section">
            {section.label && (
              <div
                data-testid="sidebar-section-label"
                className="py-2 text-xs leading-4 font-bold text-ds-text-secondary uppercase"
              >
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const isSelected = item.id === selectedId;
              const hasNotApprovedShift = item.shifts.some((shift) =>
                isShiftFlagged
                  ? isShiftFlagged(shift)
                  : (routesByShiftId.get(shift.id) ?? []).some(
                      (route) => route.status === ConnectProto.RouteStatus.NOT_APPROVED,
                    ),
              );
              const status = findPreferredShift(item.shifts).status;
              return (
                <button
                  type="button"
                  // A driver in several teams appears once per team.
                  key={`${section.key}-${item.id}`}
                  data-testid="sidebar-item"
                  aria-current={isSelected || undefined}
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    "w-full flex items-center gap-1 p-2 text-left text-sm leading-4 transition-colors",
                    variant === "ttn" ? "h-8 rounded-[8px]" : "rounded-[4px]",
                    isSelected
                      ? "bg-ds-surface-selected text-ds-text-selected font-medium tracking-[-0.2px]"
                      : "text-ds-text-body tracking-[-0.01em] hover:bg-ds-surface-secondary",
                  )}
                >
                  {variant === "routes" && (
                    <span className="h-4 w-4 shrink-0 flex items-center justify-center">
                      <span className={cn("h-2 w-2 rounded-full", shiftStatusTone(status).dot)} />
                    </span>
                  )}
                  <span className="flex-1 min-w-0 truncate">{item.label}</span>
                  {hasNotApprovedShift ? (
                    <BrandIcImportant
                      data-testid="sidebar-item-flag"
                      className="h-4 w-4 shrink-0"
                    />
                  ) : (
                    // Only a shift that ran has a TTN to approve: all-cancelled gets neither mark.
                    variant === "ttn" &&
                    item.shifts.some(
                      (shift) => shift.status !== ConnectProto.DriverOnShiftStatus.CANCELLED,
                    ) && (
                      <span
                        data-testid="sidebar-item-approved"
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] bg-ds-icon-success"
                      >
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </span>
                    )
                  )}
                  {variant === "routes" && (
                    <CountBadge
                      data-testid="sidebar-item-count"
                      // Figma Counter Badge Default on a selected row; Neutral otherwise.
                      variant={isSelected ? "ds" : "neutral"}
                      count={item.assignedJobs}
                    />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </aside>
  );
};
